import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { fetchNotificationDetails } from "../../../redux/slices/notificationsSlice";
import {
  CheckCircleIcon,
  AlertTriangleIcon,
  SendIcon,
  EditIcon,
  TrashIcon,
  EyeIcon
} from "../../../utils/svgIcons";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Accordion,
  AccordionItem,
  Tooltip,
  Spinner
} from "@heroui/react";
import { MdOutlineRemoveRedEye } from "react-icons/md";

const ALERT_TYPE_LABELS = {
  1: "EV News",
  2: "New Launches",
  3: "Offers & Discounts",
  4: "Upcoming Vehicles"
};

export default function DraftList({
  activeTab,
  drafts,
  onEdit,
  onDelete,
  onSend,
  onSendAllPerfect,
  sendingIds = {},
}) {
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [viewDetails, setViewDetails] = useState(null);
  const [loadingDetailsId, setLoadingDetailsId] = useState(null);
  const dispatch = useDispatch();

  const handleView = (id) => {
    setLoadingDetailsId(id);
    dispatch(fetchNotificationDetails(id))
      .unwrap()
      .then((data) => {
        setViewDetails(data);
      })
      .catch((err) => {
        console.error("Failed to fetch notification details:", err);
      })
      .finally(() => {
        setLoadingDetailsId(null);
      });
  };

  // const filtered = drafts.filter((d) =>
  //   activeTab === "all" ? true : d.matchStatus === activeTab
  // );
  const normalized = drafts.map((d) => ({
    id: d.id || d._id,
    title: d.title || "Untitled Notification",
    matchStatus:
      d.matchStatus || (d.review_needed === false ? "perfect" : "review_needed"),
    targetUsers: d.preference_ids?.length ?? 0,
    isCustom: d.isCustom,
    details:
      d.details ?? {
        "Alert Type": ALERT_TYPE_LABELS[d.alert_type] || d.alert_type,
        "Vehicle": d.vehicleData?.title,
        "Brand": d.vehicleData?.company_title,
        "Price (₹)": d.vehicleData?.total_price?.toLocaleString?.(),
        Link: d.link,
      },
    createdAt: d.createdAt,
  }));

  const filtered = normalized.filter((d) =>
    activeTab === "all" ? true : d.matchStatus === activeTab
  );


  return (
    <div className="space-y-6">
      {/* Send All Perfect Matches */}
      {activeTab === "perfect" &&
        drafts.filter((d) => d.matchStatus === "perfect").length > 0 && (
          <div className="flex justify-end">
            <button
              onClick={onSendAllPerfect}
              className="px-6 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-all duration-200 shadow-md flex items-center space-x-2"
            >
              <SendIcon />
              <span>Send All Perfect Matches</span>
            </button>
          </div>
        )}

      {/* Draft List */}
      {filtered.length > 0 ? (
        <Accordion variant="splitted" className="px-0 gap-4" selectionMode="multiple">
          {filtered.map((draft) => (
            <AccordionItem
              key={draft.id}
              aria-label={draft.title}
              title={
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full pr-4">
                  {/* Left: Info */}
                  <div className="flex-grow text-left">
                    <div className="flex items-center space-x-3 mb-2">
                      {draft.matchStatus === "perfect" ? (
                        <CheckCircleIcon />
                      ) : (
                        <AlertTriangleIcon />
                      )}
                      <span
                        className={`px-3 py-1 text-xs font-semibold rounded-full ${draft.matchStatus === "perfect"
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                          }`}
                      >
                        {draft.matchStatus === "perfect"
                          ? "Perfect Match"
                          : "Review Needed"}
                      </span>
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {draft.title}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      This notification will be sent to{" "}
                      <span className="font-semibold text-gray-700">
                        {draft.targetUsers?.toLocaleString?.() || "0"}
                      </span>{" "}
                      users.
                    </p>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex items-center justify-end flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Tooltip content="Send Notification" delay={0} closeDelay={0}>
                      <Button
                        onClick={() => onSend(draft.id)}
                        size="md"
                        color="primary"
                        isLoading={sendingIds[draft.id]}
                        disabled={sendingIds[draft.id]}
                        startContent={!sendingIds[draft.id] && <SendIcon />}
                        className="px-4 py-2 bg-button text-white rounded-lg text-sm font-semibold hover:bg-button/90 hover:text-white transition-all duration-200 shadow-sm flex items-center space-x-2"
                      >
                        <span>Send</span>
                      </Button>
                    </Tooltip>
                    <div className="ml-2 gap-2 border-l border-gray-200 pl-2 flex items-center">
                      <Tooltip content="View Details" delay={0} closeDelay={0}>
                        <Button
                          isIconOnly
                          variant="flat"
                          size="md"
                          isLoading={loadingDetailsId === draft.id}
                          onClick={() => handleView(draft.id)}
                          className="p-2 text-gray-500 rounded-lg hover:bg-gray-100 hover:text-gray-800 transition-all duration-200 flex items-center justify-center"
                        >
                          {loadingDetailsId !== draft.id ? <MdOutlineRemoveRedEye className="w-4 h-4" /> : <Spinner size="sm" color="current" />}
                        </Button>
                      </Tooltip>
                      <Tooltip content="Edit Notification" delay={0} closeDelay={0}>
                        <Button
                          isIconOnly
                          variant="flat"
                          size="md"
                          onClick={() => onEdit(drafts.find(d => (d.id || d._id) === draft.id) || draft)}
                          className="p-2 text-gray-500 rounded-lg hover:bg-gray-100 hover:text-gray-800 transition-all duration-200"
                        >
                          <EditIcon />
                        </Button>
                      </Tooltip>
                      <Tooltip content="Delete Notification" delay={0} closeDelay={0}>
                        <Button
                          isIconOnly
                          variant="flat"
                          size="md"
                          onClick={() => setConfirmDeleteId(draft.id)}
                          className="p-2 text-gray-500 rounded-lg hover:bg-red-100 hover:text-red-600 transition-all duration-200"
                        >
                          <TrashIcon />
                        </Button>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              }
              classNames={{
                base: "bg-white !p-0 !rounded-xl shadow-md overflow-hidden border border-transparent select-text",
                title: "w-full select-text",
                trigger: "px-6 py-4 !select-text",
                content: "px-6 pb-6 pt-0 border-t border-gray-100 mt-2 select-text",
              }}
            >
              {/* Details */}
              <div className="pt-4">
                <h4 className="font-semibold text-gray-700 mb-2">
                  Notification Data
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 text-sm">
                  {Object.entries(draft.details || {})
                    .filter(([key]) => !(draft.isCustom && (key === "Vehicle" || key === "Brand" || key === "Price (₹)")))
                    .map(([key, value]) => (
                      <div key={key}>
                        <p className="text-gray-500">{key}</p>
                        <p className="font-medium text-gray-800">{value}</p>
                      </div>
                    ))}
                </div>
              </div>
              <div className="bg-gray-50 -mx-6 -mb-6 px-6 py-2 text-right mt-6 border-t border-gray-100">
                <p className="text-xs text-gray-400">
                  Generated: {draft.createdAt ? new Date(draft.createdAt).toLocaleString() : "N/A"}
                </p>
              </div>
            </AccordionItem>
          ))}
        </Accordion>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border">
          <h3 className="text-lg font-medium text-gray-700">
            No drafts to show
          </h3>
          <p className="text-gray-500 mt-1">
            There are no drafts matching the selected filter.
          </p>
        </div>
      )}

      {confirmDeleteId && (
        <Modal isOpen backdrop="blur" onOpenChange={() => setConfirmDeleteId(null)}>
          <ModalContent>
            <ModalHeader>
              <span className="text-xl font-semibold text-gray-900">Delete Notification</span>
            </ModalHeader>
            <ModalBody>
              <p className="text-sm text-gray-700">Are you sure you want to delete this notification?</p>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="flat"
                color="default"
                onPress={() => setConfirmDeleteId(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
              >
                No
              </Button>
              <Button
                color="danger"
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                onPress={() => {
                  onDelete(confirmDeleteId);
                  setConfirmDeleteId(null);
                }}
              >
                Yes
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {viewDetails && (
        <Modal
          isOpen
          size="3xl"
          scrollBehavior="inside"
          backdrop="blur"
          onOpenChange={() => setViewDetails(null)}
          classNames={{
            base: "max-w-[850px] bg-white rounded-2xl shadow-2xl border border-gray-100",
            header: "border-b border-gray-100 px-6 py-4 bg-gray-50/50 rounded-t-2xl",
            body: "p-6",
            footer: "border-t border-gray-100 px-6 py-4 bg-gray-50/50 rounded-b-2xl",
          }}
        >
          <ModalContent>
            <ModalHeader className="flex flex-col gap-1">
              <div className="flex items-center space-x-3">
                <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${viewDetails.review_needed
                  ? "bg-red-100 text-red-800"
                  : "bg-green-100 text-green-800"
                  }`}>
                  {viewDetails.review_needed ? "Review Needed" : "Perfect Match"}
                </span>
                <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                  {ALERT_TYPE_LABELS[viewDetails.alert_type] || "General Alert"}
                </span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mt-2 select-text">
                {viewDetails.title}
              </h2>
            </ModalHeader>
            <ModalBody className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 space-y-4">
                  {viewDetails.image_url && (
                    <div className="rounded-xl overflow-hidden border border-gray-200 bg-gray-50 aspect-video flex items-center justify-center">
                      <img
                        src={viewDetails.image_url}
                        alt={viewDetails.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 tracking-wider mb-2">
                      Description
                    </h3>
                    <div
                      className="prose prose-sm max-w-none text-gray-800 bg-gray-50 p-4 rounded-xl border border-gray-100 select-text"
                      dangerouslySetInnerHTML={{ __html: viewDetails.description }}
                    />
                  </div>
                  {viewDetails.link && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 tracking-wider mb-1">
                        Target Action Link
                      </h3>
                      <a
                        href={viewDetails.link}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:text-blue-800 font-medium break-all underline text-sm select-text"
                      >
                        {viewDetails.link}
                      </a>
                    </div>
                  )}
                </div>

                <div className="space-y-4 bg-gray-50/50 p-4 rounded-xl border border-gray-100">
                  <div>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                      Created At
                    </h3>
                    <p className="text-sm text-gray-700 font-medium select-text">
                      {new Date(viewDetails.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                      Last Updated
                    </h3>
                    <p className="text-sm text-gray-700 font-medium select-text">
                      {new Date(viewDetails.updatedAt).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                      Custom Created
                    </h3>
                    <p className="text-sm text-gray-700 font-medium">
                      {viewDetails.isCustom ? "Yes" : "No"}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                      Target Preferences
                    </h3>
                    <p className="text-sm text-gray-700 font-medium select-text">
                      {viewDetails.preference_ids?.length || 0} user preferences
                    </p>
                  </div>

                  {viewDetails.vehicleData && (
                    <div className="pt-2 border-t border-gray-200 mt-2">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        Associated Vehicle
                      </h4>
                      <div className="text-sm space-y-1">
                        <p className="font-semibold text-gray-800 select-text">
                          {viewDetails.vehicleData.title}
                        </p>
                        <p className="text-xs text-gray-500">
                          Brand: <span className="font-medium text-gray-700 select-text">{viewDetails.vehicleData.company_title}</span>
                        </p>
                        <p className="text-xs text-gray-500">
                          Price: <span className="font-medium text-gray-700 select-text">₹{viewDetails.vehicleData.total_price?.toLocaleString()}</span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-100 pt-6">
                <h3 className="text-base font-bold text-gray-800 mb-3 flex items-center space-x-2">
                  <span>Target Reach Info</span>
                  <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full font-semibold">
                    {((viewDetails.matched_emails?.length || 0) + (viewDetails.matched_phones?.length || 0) + (viewDetails.matched_browser_ids?.length || 0))} matches
                  </span>
                </h3>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                      Emails ({viewDetails.matched_emails?.length || 0})
                    </h4>
                    {viewDetails.matched_emails && viewDetails.matched_emails.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto p-2 bg-gray-50 rounded-lg border border-gray-100 select-text">
                        {viewDetails.matched_emails.map((email, idx) => (
                          <span key={idx} className="bg-white border border-gray-200 px-2 py-0.5 rounded text-xs text-gray-600 font-medium">
                            {email}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 italic">No emails matched</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        Phone Numbers ({viewDetails.matched_phones?.length || 0})
                      </h4>
                      {viewDetails.matched_phones && viewDetails.matched_phones.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto p-2 bg-gray-50 rounded-lg border border-gray-100 select-text">
                          {viewDetails.matched_phones.map((phone, idx) => (
                            <span key={idx} className="bg-white border border-gray-200 px-2 py-0.5 rounded text-xs text-gray-600 font-medium">
                              {phone}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 italic">No phone numbers matched</p>
                      )}
                    </div>

                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        Browser Notifications ({viewDetails.matched_browser_ids?.length || 0})
                      </h4>
                      {viewDetails.matched_browser_ids && viewDetails.matched_browser_ids.length > 0 ? (
                        <div className="p-2 bg-gray-50 rounded-lg border border-gray-100 text-xs text-gray-600 select-text font-medium">
                          Targeted {viewDetails.matched_browser_ids.length} browser subscriber endpoints.
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 italic">No browser notification endpoints matched</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                color="primary"
                onPress={() => setViewDetails(null)}
                className="px-6 py-2 bg-button text-white rounded-lg font-semibold hover:bg-button/90"
              >
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </div>
  );
}
